import { UnitActionType } from "../../Controls/FormParts/Unit/Store/unitActions";
import {
    findFreeInstanceNumber,
    getInstancesAfterAdd,
    getInstancesAfterRemove,
} from "../../Helpers/StateAccessors/MultilineHelpers";
import { currentViewModelSelect } from "../../Helpers/StateAccessors/ViewModelSelect";
import { formPageChangedEvent } from "../Core/EventManagement/ExternalEvents/ExternalEvents";
import { CandyThunkAction } from "../Core/Store/CandyReduxTypes";
import {
    createUnitId,
    extractInstanceFromBindingPath,
    SectionName,
    UnitId,
    unitIdWithInstance,
} from "../../Controls/FormParts/Unit/UnitId/UnitId";
import { ModelHunk } from "../Core/ModelStoring/ModelHunk";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    createAbsolute,
    createModelPath,
    PathTokens,
} from "../../../../Common/ModelPath/ModelPath";
import { nextTick } from "../../../../Common/AsyncUtils";
import { isAggregationPath } from "../../../../Common/ModelPath/ModelPathHelper";
import { UIUtil } from "../Form/CustomizationContexts/HelperFunctionContext/UIUtil/UIUtil";
import { reject } from "../../../../Common/TypingUtils";
import { IterUtils } from "../../../../Common/IterableUtils";
import { BindingPath } from "../Core/CoreTypes";
import { UnitInfo } from "../../Controls/UnitInfo";

import { scrollToUnitAction, unitHighlightAction } from "./NavigationActionCreators";

export enum ClearPageMethod {
    OnlyParent = "OnlyParent",
    ParentAndChildren = "ParentAndChildren",
}

export function addPageAction(unitType: SectionName): CandyThunkAction {
    return async (_, __, form) => {
        await form.normalizeSection(unitType);
        form.triggerExternalEvent(formPageChangedEvent(unitType));
    };
}

const UNITS_COUNT_SERVER_REMOVE_THRESHOLD = 10;

export function removePageAction(unitType: SectionName, bindingPath?: AbsoluteResolvedModelPath): CandyThunkAction {
    return async (_, getState, form) => {
        if (bindingPath && form.settings.useServerSideFLangNormalization) {
            const instances = currentViewModelSelect.children(getState(), bindingPath);
            if (instances?.length > UNITS_COUNT_SERVER_REMOVE_THRESHOLD) {
                const uiUtil = new UIUtil(form.settings.theme);
                await uiUtil.executeLongOperation(
                    { title: "Удаление раздела", modalSpecialCase: "InfiniteProcess", allowCancel: false },
                    async () => {
                        await form.candyApiDraftClient.deleteInstances([
                            {
                                multiplicityPath: bindingPath.toLegacyPath(),
                                instanceNumbers: instances as string[],
                                prefixesToDelete: [],
                            },
                        ]);
                        await form.reloadInner();
                    }
                );
            }
        }
        form.removeSection(unitType, bindingPath);
        form.triggerExternalEvent(formPageChangedEvent(unitType));
    };
}

export function clearPageAction(
    unitType: SectionName,
    bindingPath: AbsoluteResolvedModelPath | undefined,
    method: ClearPageMethod
): CandyThunkAction {
    return async (_, __, form) => {
        await form.clearSection(unitType, bindingPath, method);
    };
}

export function addMultipleUnitInstanceAction(
    bindingPath: AbsoluteResolvedModelPath,
    unitId: UnitId,
    newIndex?: number
): CandyThunkAction {
    return (dispatch, getState, form) => {
        const instances = currentViewModelSelect.children(getState(), bindingPath);
        const newInstance = findFreeInstanceNumber(instances ?? []);
        const newInstances = getInstancesAfterAdd(instances, newInstance, newIndex ? newIndex : instances.length);
        form.setChildren(bindingPath, newInstances);
        dispatch({
            type: UnitActionType.AddUnit,
            payload: {
                bindingPath: bindingPath.toLegacyPath(),
                newInstance: newInstance,
                newIndex: newIndex,
                unitId: unitId,
            },
        });
        setTimeout(() => {
            const id = unitIdWithInstance(unitId, newInstance);
            dispatch(scrollToUnitAction(id));
            dispatch(unitHighlightAction(id));
        }, 0);
    };
}

export function clearMultipleUnitInstanceAction(
    unitType: SectionName,
    bindingPath: AbsoluteResolvedModelPath,
    removingInstanceId: string,
    method: ClearPageMethod
): CandyThunkAction {
    return async (_, getState, form) => {
        if (method === ClearPageMethod.ParentAndChildren) {
            const instances = currentViewModelSelect.children(getState(), bindingPath);
            const oldInstances = [...instances];
            const newInstances = getInstancesAfterRemove(instances, [removingInstanceId]);
            form.setChildren(bindingPath, newInstances);
            form.setChildren(bindingPath, oldInstances);

            const parentInstances = Iterator.from(bindingPath.getPathParts())
                .filter(part => PathTokens.isInstanceToken(part))
                .toArray()
                .reverse();
            const changedSections = form.getSectionsByPath(bindingPath, [removingInstanceId].concat(parentInstances));
            for (const section of changedSections) {
                form.triggerExternalEvent(formPageChangedEvent(section));
            }
        } else {
            const instancePath = bindingPath.joinWith(removingInstanceId);
            await form.renormalizePage(unitType, instancePath);
        }
    };
}

export function removeMultipleUnitInstanceAction(
    bindingPath: AbsoluteResolvedModelPath,
    instance: string,
    unitId: UnitId,
    scrollToNearestInstance: boolean
): CandyThunkAction {
    return async (dispatch, getState, form) => {
        const instances = currentViewModelSelect.children(getState(), bindingPath);
        const unitIdToScrollAfter = scrollToNearestInstance
            ? findScrolledUnitAfterRemoveUnit(instances, unitId, instance)
            : undefined;
        form.setChildren(bindingPath, getInstancesAfterRemove(instances, [instance]));
        form.filteringManager.handleRemoveInstances(bindingPath, [instance]);
        dispatch({
            type: UnitActionType.RemoveUnit,
            payload: {
                bindingPath: bindingPath.toLegacyPath(),
                index: instance,
                unitId: unitId,
            },
        });
        if (unitIdToScrollAfter != undefined) {
            dispatch(scrollToUnitAction(unitIdToScrollAfter));
            dispatch(unitHighlightAction(unitIdToScrollAfter));
        }
    };
}

export function copyMultipleUnitInstanceAction(
    bindingPath: AbsoluteResolvedModelPath,
    sourceInstance: string,
    unitId: UnitId
): CandyThunkAction<Promise<UnitId>> {
    return async (dispatch, getState, form) => {
        const instancePath = bindingPath.joinWith(sourceInstance);
        const instances = currentViewModelSelect.children(getState(), bindingPath);
        const instance = findFreeInstanceNumber(instances);
        const instanceIndex = instances.length;
        const newInstances = getInstancesAfterAdd(instances, instance, instanceIndex);
        const prefix = bindingPath.joinWith(instance);

        const dataWithRelativePaths = ModelHunk.create();
        const secondDataWithRelativePaths = ModelHunk.create();
        const instanceData = form.getBatch(instancePath);
        const pathsToCopy = ModelHunk.paths(instanceData).map<[string, string]>(absolutePath => [
            absolutePath,
            absolutePath.replace(instancePath.toLegacyPath() + "/", ""),
        ]);
        for (const [absolutePath, relativePath] of pathsToCopy) {
            if (!isAggregationPath(createModelPath(absolutePath, true))) {
                const viewModel = { ...ModelHunk.get(instanceData, absolutePath) };
                if (viewModel.autoValue == undefined) {
                    ModelHunk.set(
                        dataWithRelativePaths,
                        prefix.joinWith(createModelPath(relativePath, false)),
                        viewModel
                    );
                } else {
                    ModelHunk.set(
                        secondDataWithRelativePaths,
                        prefix.joinWith(createModelPath(relativePath, false)),
                        viewModel
                    );
                }
            }
        }
        form.updateModel(model => {
            model.setChildren(bindingPath, newInstances);
        });

        form.updateModel(model => {
            model.setBatch(dataWithRelativePaths);
        });

        form.updateModel(model => {
            model.setBatch(secondDataWithRelativePaths);
        });

        form.sections.resolveOptionalSections(dataWithRelativePaths);
        form.sections.resolveOptionalSections(secondDataWithRelativePaths);

        dispatch({
            type: UnitActionType.AddUnit,
            payload: {
                bindingPath: bindingPath.toLegacyPath(),
                newInstance: instance,
                newIndex: instanceIndex,
                unitId: unitId,
            },
        });
        await nextTick();
        const copiedUnitId = createUnitId(unitId.pageId, instance);
        copiedUnitId.parentInstances = unitId.parentInstances ? [...unitId.parentInstances] : [];
        dispatch(scrollToUnitAction(copiedUnitId));
        dispatch(unitHighlightAction(copiedUnitId));
        return copiedUnitId;
    };
}

function findScrolledUnitAfterRemoveUnit(
    instances: readonly string[],
    unitId: UnitId,
    instanceToRemove: string
): UnitId | undefined {
    if (instances.length > 1) {
        const instanceIndex = instances.findIndex(ins => ins === instanceToRemove);
        return instanceIndex === 0
            ? unitIdWithInstance(unitId, instances[instanceIndex + 1])
            : unitIdWithInstance(unitId, instances[instanceIndex - 1]);
    }

    return undefined;
}

function closeAllModalUnits(ignoredUnit?: UnitInfo): CandyThunkAction {
    return (_, getState, form) => {
        const unitInfos = getState().unitInfos;
        const unitsWithModals = unitInfos?.filter(x => x.modalInstancePageId) ?? [];
        for (const unit of unitsWithModals) {
            if (unit !== ignoredUnit) {
                form.closeModalUnit({ pageId: unit.id });
            }
        }
    };
}

export function openModalUnitAction(
    helperContextualBindingPath: AbsoluteModelPath,
    unitId: UnitId,
    modalInstancePageId: string
): CandyThunkAction {
    return (_, getState, form) => {
        const unitInfos = getState().unitInfos;
        const nodalUnitInfo =
            unitInfos?.find(x => x.id === modalInstancePageId) ??
            reject(`Modal instance page [${modalInstancePageId}] is not found !`);
        const bindingPathParts = helperContextualBindingPath.getPathPartsAsArray();
        const nodalUnitInfoBindingPathLength = IterUtils.count(nodalUnitInfo.bindingPath.getPathParts());
        const instanceSource = bindingPathParts[nodalUnitInfoBindingPathLength];
        const instance = PathTokens.isInstanceToken(instanceSource) ? instanceSource : reject("Instance is not found!");
        const modalUnitId: UnitId = { pageId: modalInstancePageId, instance: instance };
        form.openModalUnit(unitId, modalUnitId);
    };
}

export function openModalUnitFromErrorLogAction(unitId: UnitId, path: BindingPath): CandyThunkAction {
    return (_, getState, form) => {
        const unitInfos = getState().unitInfos;
        const currentUnit = unitInfos?.find(x => x.modalInstancePageId === unitId.pageId);
        if (!currentUnit) {
            throw new Error("Target unit (attribute modalInstancePageId) is not found !");
        }
        const targetUnitId = currentUnit.multiple
            ? {
                  pageId: currentUnit.id,
                  instance: extractInstanceFromBindingPath(
                      createAbsolute(path),
                      {
                          pageId: currentUnit.id,
                      },
                      unitInfos
                  ),
              }
            : {
                  pageId: currentUnit.id,
              };

        //проверка unitIsExists нужна для форм, где показ какого-либо раздела регулируется в if condition
        const pathToFlag = currentUnit.bindingPath.toString() + "/" + currentUnit.isUnitExistsFlag;
        if (currentUnit.isUnitExistsFlag) {
            if (form.getValue(pathToFlag) !== "true") {
                return;
            }
            closeAllModalUnits(currentUnit);
        }
        form.openModalUnit(targetUnitId, unitId, false);
    };
}

export function closeModalUnitAction(unitId: UnitId): CandyThunkAction {
    return (_, __, form) => {
        form.closeModalUnit(unitId);
    };
}
